import {
  Account,
  AccountBranding,
  AccountCourseDelivery,
} from '@/client/accounts';
import {
  UserManagementFormIndexValueMapping,
  UserManagementMapping,
  UserManagementMeta,
} from '@/client/accounts/types/account-user-management.type';
import { MaterialType } from '@/client/courses';
import { SystemRoles, User } from '@/client/users';
import { TranslationFunctionType } from '@/common/types';
import { isArray, isEmpty } from 'lodash';
import { v4 } from 'uuid';
import { getHubspotIsActiveStatus } from '../hubspot/hubspot.helper';

export const accountHasDocuments = (account: Account | null): boolean =>
  !!account?.availableCoursesByType &&
  !!Object.keys(account?.availableCoursesByType).find(
    (x) => (MaterialType.RESOURCE as string) === x,
  );

export const courseDeliveryLmsOptions = (
  t: TranslationFunctionType,
  isFilter?: boolean,
) => [
  ...(isFilter
    ? [
        {
          label: t('cp.app'),
          value: AccountCourseDelivery.CP_APP,
        },
      ]
    : []),
  {
    label: t('account.courseDelivery.thirdParty'),
    value: AccountCourseDelivery.THIRD_PARTY,
  },
  {
    label: t('account.courseDelivery.videbarometer'),
    value: AccountCourseDelivery.VIDENBAROMETER,
  },
];

export const displayLmsOption = (t: TranslationFunctionType) => ({
  [AccountCourseDelivery.CP_APP]: t('cp.app'),
  [AccountCourseDelivery.THIRD_PARTY]: t('account.courseDelivery.thirdParty'),
  [AccountCourseDelivery.VIDENBAROMETER]: t(
    'account.courseDelivery.videbarometer',
  ),
});

export const branchAdminCheck = (
  user: User | null,
  currentAccount: Account | null,
) =>
  !!user?.role?.code &&
  [SystemRoles.ADMIN, SystemRoles.MULTIPLE_ACCOUNTS_ADMIN].includes(
    user?.role?.code,
  ) &&
  !!user.branch?.id &&
  user.account.id === currentAccount?.id;

export const isMultiAdminRestriction = (
  selectedUser?: User,
  currentUser?: User | null,
) =>
  Boolean(
    selectedUser?.role?.code === SystemRoles.MULTIPLE_ACCOUNTS_ADMIN &&
      currentUser &&
      ![SystemRoles.DEVELOPER, SystemRoles.SUPER_ADMIN].includes(
        currentUser?.role?.code,
      ),
  );

export const getAccountIntegration = (
  account?: Account | null,
): UserManagementMeta | undefined => {
  if (!account?.activeDirectoryIntegration?.length) {
    return undefined;
  }

  return account?.activeDirectoryIntegration?.find(
    (integration) => integration.active,
  );
};

export const transformMappingValues = (
  data: UserManagementMapping[],
  initialMapping: UserManagementFormIndexValueMapping,
  currentAccount: Account | null,
  account: Account,
): UserManagementFormIndexValueMapping => {
  if (!data?.length) return initialMapping;

  const mappedObject: UserManagementFormIndexValueMapping = {};

  data.map((value) => {
    mappedObject[v4()] = {
      ...value,
      ...(!value.branches.length && currentAccount ? { account: account } : {}),
    };
  });
  return mappedObject;
};

export const showItSupport = (data: AccountBranding | undefined) => {
  if (!data) return false;
  return (
    !!data.itSupportName ||
    !!data.itSupportTitle ||
    !!data.itSupportEmail ||
    !!data.itSupportPhone
  );
};

enum ErrorCodes {
  USER_ALREADY_ASSIGNED_TO_BRANCH = 'user.already.assigned.to.branch',
  USER_ALREADY_EXISTS_IN_ANOTHER_ACCOUNT = 'user.already.exists.in.another.account',
  USER_NO_EMAIL_PROVIDED = 'user.no.email.provided',
  USER_DUPLICATE_USERNAME = 'user.duplicate.username',
}

export const getErrorMessageFromADSync = (
  error: string | null | undefined,
  t: TranslationFunctionType,
) => {
  if (!error) return undefined;
  let parsedError: string | unknown[] = error;

  if (typeof error === 'string') {
    try {
      parsedError = JSON.parse(error);
    } catch {
      return error;
    }
  }

  const modifiedErrors: string[] = [];

  if (isArray(parsedError) && !isEmpty(parsedError)) {
    parsedError.forEach((error: unknown) => {
      if (!error) return;

      if (!isArray(error)) {
        return modifiedErrors.push(error as string);
      }

      const [errorMessage, errorCode, params] = error as [
        string,
        string,
        object,
      ];

      switch (errorCode) {
        case ErrorCodes.USER_ALREADY_ASSIGNED_TO_BRANCH: {
          const [username, branchName] = params as [string, string];
          modifiedErrors.push(
            t('account.azureAD.syncError.alreadyAssignedToBranch', {
              username,
              branchName,
            }),
          );
          break;
        }

        case ErrorCodes.USER_ALREADY_EXISTS_IN_ANOTHER_ACCOUNT: {
          const [email, groupId, accountName] = params as [
            string,
            string,
            string,
          ];
          modifiedErrors.push(
            t('account.azureAD.syncError.alreadyExistsInAnotherAccount', {
              email,
              groupId,
              accountName,
            }),
          );
          break;
        }

        case ErrorCodes.USER_NO_EMAIL_PROVIDED: {
          const [firstName, lastName] = params as [string, string];
          modifiedErrors.push(
            t('account.azureAD.syncError.noEmailProvided', {
              firstName,
              lastName,
            }),
          );
          break;
        }

        case ErrorCodes.USER_DUPLICATE_USERNAME: {
          const [firstName, lastName, email] = params as [
            string,
            string,
            string,
          ];
          modifiedErrors.push(
            t('account.azureAD.syncError.duplicate', {
              firstName,
              lastName,
              email,
            }),
          );
          break;
        }

        default:
          modifiedErrors.push(errorMessage);
          break;
      }
    });
  }

  return modifiedErrors.length ? modifiedErrors.join('\n') : undefined;
};

export const getIsActivePhishingStatus = (
  account: Account | undefined | null,
): boolean =>
  getHubspotIsActiveStatus(account?.hubspot?.phishingStatus) ||
  getHubspotIsActiveStatus(account?.hubspot?.autoPhishingStatus) ||
  getHubspotIsActiveStatus(account?.hubspot?.phishingOneOffStatus);

export const getIsPhishingOnlyStatus = (account: Account | undefined) => {
  if (!account?.hubspot) return false;
  const isAWTCustomer = getHubspotIsActiveStatus(account?.hubspot?.awtStatus);
  const isPhishingCustomer = getIsActivePhishingStatus(account);
  const isSCCustomer = getHubspotIsActiveStatus(
    account?.hubspot?.securityCultureStatus,
  );
  const isPhishingOnly = isPhishingCustomer && !isAWTCustomer && !isSCCustomer;
  return isPhishingOnly;
};

export const showBlockEmailsMessage = (account: Account | undefined | null) => {
  if (!account) return false;
  return !!account?.blockSystemEmails && !getIsPhishingOnlyStatus(account);
};
